/**
 * Agent Channels List Component
 * Displays channels linked to an agent with add and delete actions
 */

import {
  Stack,
  Title,
  Card,
  Icon,
  useToast,
  useActions,
  useTheme,
  P,
  ActionMenu,
  Accordion
} from "@introist/react-foundation/v2";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi, AgentChannel } from "../api";
import { useAgentChannels } from "../hooks/use-agent-channels";
import { useChannels } from "../hooks/use-channels";
import { Skeletons } from "../../components/molecules";
import { IconButtonSelect } from "../../foundation/blocks/IconButtonSelect";
import { getPlatformIcon } from "./PlatformIcon";
import { useMemo } from "react";
import { SelectorIcon } from "../../modules/employees/fields/EmployeeFieldSelector";

interface AgentChannelsListProps {
  agentConfigId: string;
}

export const AgentChannelsList = ({ agentConfigId }: AgentChannelsListProps) => {
  const { data: agentChannels = [], isLoading } = useAgentChannels(agentConfigId);
  const { data: allChannels = [] } = useChannels();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const { theme } = useTheme();

  // Get available channels (not yet linked to this agent)
  const availableChannels = useMemo(() => {
    const linkedChannelIds = new Set(agentChannels.map(c => c.id));
    return allChannels.filter(channel => !linkedChannelIds.has(channel.id));
  }, [agentChannels, allChannels]);

  const addChannelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      return agentsApi.addChannel(agentConfigId, { channelId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "agents", agentConfigId, "channels"]
      });
      toast.success("Channel added successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to add channel: ${error.message}`);
    }
  });

  const removeChannelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      return agentsApi.removeChannel(agentConfigId, channelId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "agents", agentConfigId, "channels"]
      });
      toast.success("Channel removed successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to remove channel: ${error.message}`);
    }
  });

  const handleAddChannel = async (channelId: string) => {
    await addChannelMutation.mutateAsync(channelId);
  };

  const handleRemove = (channel: AgentChannel) => {
    return onConfirmAction(
      async () => {
        await removeChannelMutation.mutateAsync(channel.id);
      },
      {
        title: "Remove channel",
        description: `Are you sure you want to remove ${channel.name} from this agent?`,
        confirmTitle: "Remove"
      }
    );
  };

  if (isLoading) {
    return <Skeletons.List rowCount={2} rowHeight={80} />;
  }

  return (
    <Accordion
      title="Channels"
      description={
        agentChannels.length > 0
          ? agentChannels.map(c => c.name).join(",")
          : "Where this agent responds to messages"
      }
      icon={<SelectorIcon icon="comment" />}
      compact
      defaultOpen={false}
      toggleOpenOnHeaderClick
    >
      <Stack vertical style={{ gap: "var(--spacing-small)" }}>
        {availableChannels.length > 0 && (
          <Stack style={{ justifyContent: "flex-end" }}>
            <IconButtonSelect
              icon="plus"
              variant="blended"
              size="small"
              onChange={handleAddChannel}
              loading={addChannelMutation.isLoading}
              options={availableChannels.map(channel => ({
                key: channel.id,
                title: channel.name,
                startAdornment: getPlatformIcon(channel.platform)
              }))}
            />
          </Stack>
        )}

        {agentChannels.length === 0 ? (
          <Card>
            <Stack
              vertical
              style={{
                alignItems: "center",
                gap: "var(--spacing-small)",
                padding: "var(--spacing-medium)"
              }}
            >
              <P style={{ color: "var(--palette-foreground-subdued)" }}>No channels linked yet</P>
            </Stack>
          </Card>
        ) : (
          <Stack vertical style={{ gap: "var(--spacing-small)" }}>
            {agentChannels.map(channel => (
              <Card key={channel.id}>
                <Stack
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {getPlatformIcon(channel.platform)}
                  </div>
                  <Stack
                    vertical
                    style={{
                      gap: "var(--spacing-xxSmall)",
                      flex: 1,
                      marginLeft: "var(--spacing-medium)"
                    }}
                  >
                    <Title variant="bold">{channel.name}</Title>
                  </Stack>
                  <ActionMenu
                    options={[
                      {
                        key: "remove",
                        title: "Remove",
                        onClick: handleRemove(channel),
                        startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
                        titleStyles: { color: "var(--palette-danger-default)" }
                      }
                    ]}
                  />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Accordion>
  );
};
